import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { disconnectSupportSocket, isSupportSocketConnected } from '../services/supportSocketService';
import { initializeFCM, getFCMToken, setupForegroundMessageHandler, isNotificationSupported } from '../services/fcmService';
import firebaseApp, { VAPID_KEY } from '../config/firebase';
import { syncFCMTokenToProfile } from '../utils/initializeFCM';
import { getSocket, disconnectSocket } from '../socket';
import { toast } from 'react-toastify';

const SupportSocketContext = createContext(null);

export const useSupportSocket = () => {
    const context = useContext(SupportSocketContext);
    if (!context) {
        console.warn('useSupportSocket must be used within SupportSocketProvider');
        // Return a default object to prevent destructuring errors
        return { isConnected: false };
    }
    return context;
};

export const SupportSocketProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const socketInitialized = useRef(false);
    const checkInterval = useRef(null);

    useEffect(() => {
        // Check if user is logged in
        const checkAndInitialize = () => {
            const userId = localStorage.getItem('ID');
            const userRole = localStorage.getItem('Role');
            const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

            if (!userId || !userRole || !isAuthenticated) {
                console.log('📵 No user logged in, skipping socket initialization');
                return;
            }

            // Skip if already initialized (FCM only, not socket)
            if (socketInitialized.current) {
                console.log('✅ FCM services already initialized');
                return;
            }

            console.log('🔌 SupportSocketProvider: Initializing FCM services (socket will connect when support page opens)');
            socketInitialized.current = true;

            initializeServicesNow();
        };

        // Track last seen unread counts to detect new messages
        const lastUnreadRef = {};

        // Connect chat socket globally so we receive chatListUpdate on any page
        const connectChatSocketGlobally = () => {
            const userRole = localStorage.getItem('Role');
            const chatSocket = getSocket();
            if (!chatSocket) return;

            // Listen for new message updates globally
            const handleGlobalChatUpdate = (data) => {
                // Determine the chat partner key and current unread
                let chatKey, currentUnread, partnerName;
                if (userRole === 'doctor') {
                    chatKey = data.patientId;
                    currentUnread = data.unreadForDoctor ?? data.unread ?? 0;
                    partnerName = data.patient?.name || data.name || 'Patient';
                } else {
                    chatKey = data.doctorId;
                    currentUnread = data.unreadForPatient ?? data.unread ?? 0;
                    partnerName = data.doctor?.name || data.name || 'Doctor';
                    if (data.doctor?.name || data.name) {
                        partnerName = 'Dr. ' + partnerName;
                    }
                }

                if (!chatKey) return;

                const prevUnread = lastUnreadRef[chatKey] || 0;

                // Only notify if unread increased (new message arrived)
                if (currentUnread > prevUnread) {
                    const chatPath = userRole === 'doctor' ? '/patientchat' : '/doctorchat';
                    const currentHash = window.location.hash || '';
                    const isOnChatPage = currentHash.includes('patientchat') || currentHash.includes('doctorchat');

                    // Don't show toast if user is already on the chat page
                    if (!isOnChatPage) {
                        let msgPreview = 'New message';
                        if (data.lastMessage?.message) {
                            msgPreview = data.lastMessage.message.length > 40
                                ? data.lastMessage.message.substring(0, 40) + '...'
                                : data.lastMessage.message;
                        } else if (data.lastMessage?.attachments?.length > 0) {
                            const fileType = data.lastMessage.attachments[0]?.fileType;
                            msgPreview = fileType === 'image' ? 'Photo' : fileType === 'voice' ? 'Voice message' : 'File';
                        }

                        toast.info(
                            `${partnerName}: ${msgPreview}`,
                            {
                                autoClose: 4000,
                                onClick: () => {
                                    window.location.hash = chatPath;
                                }
                            }
                        );
                    }
                }

                lastUnreadRef[chatKey] = currentUnread;
            };

            chatSocket.off('chatListUpdate', handleGlobalChatUpdate);
            chatSocket.on('chatListUpdate', handleGlobalChatUpdate);

            // Connect if not already connected
            if (!chatSocket.connected) {
                chatSocket.connect();
            }
        };

        // Initialize services function
        const initializeServicesNow = () => {

            // Initialize FCM only (socket will connect when support page opens)
            const initializeServices = async () => {
                const userId = localStorage.getItem('ID');
                const userRole = localStorage.getItem('Role');

                try {
                    // Check if browser supports FCM before attempting initialization
                    if (!isNotificationSupported()) {
                        console.warn('⚠️ FCM: Browser does not support notifications. Skipping FCM initialization.');
                        return;
                    }

                    // Initialize Firebase Cloud Messaging
                    console.log('🔔 Initializing Firebase Cloud Messaging...');
                    const messaging = await initializeFCM(firebaseApp);
                    
                    if (messaging) {
                        // Get FCM token
                        const token = await getFCMToken(VAPID_KEY);
                        if (token) {
                            console.log('FCM initialized and token obtained');

                            // Sync FCM token to Doctor/Patient model for chat notifications
                            syncFCMTokenToProfile();

                            // Setup foreground message handler
                            setupForegroundMessageHandler((payload) => {
                                console.log('Foreground notification received:', payload);
                            });
                        } else {
                            console.warn('⚠️ FCM initialized but no token obtained');
                        }
                    } else {
                        console.warn('⚠️ FCM initialization failed or permission denied');
                    }
                } catch (error) {
                    // Handle errors gracefully - don't break the app if FCM fails
                    if (error.code === 'messaging/unsupported-browser') {
                        console.warn('⚠️ FCM: Browser does not support Firebase Messaging');
                    } else {
                        console.error('❌ Error initializing FCM:', error);
                    }
                } finally {
                    // Always connect chat socket globally, regardless of FCM status
                    connectChatSocketGlobally();
                }
            };

            // Start initialization
            initializeServices();

            // Check connection status periodically (but don't auto-connect)
            checkInterval.current = setInterval(() => {
                const connected = isSupportSocketConnected();
                setIsConnected(connected);
                // Don't auto-reconnect - let support pages handle connection
            }, 2000); // Check every 2 seconds
        };

        // Check immediately on mount
        checkAndInitialize();

        // Listen for storage changes (login/logout events)
        const handleStorageChange = (e) => {
            if (e.key === 'isAuthenticated' || e.key === 'ID') {
                console.log('🔄 Authentication state changed, re-checking...');
                checkAndInitialize();
            }
        };

        // Listen for custom login event
        const handleLoginEvent = () => {
            console.log('🔄 Login event detected, initializing FCM services (socket will connect when support page opens)...');
            setTimeout(() => {
                checkAndInitialize();
            }, 500); // Small delay to ensure localStorage is updated
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('userLoggedIn', handleLoginEvent);

        // Cleanup on unmount
        return () => {
            console.log('🧹 SupportSocketProvider: Cleaning up');
            if (checkInterval.current) {
                clearInterval(checkInterval.current);
            }
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('userLoggedIn', handleLoginEvent);
        };
    }, []); // Empty dependency array - listeners handle updates

    // Listen for logout events and page unload
    useEffect(() => {
        const handleLogout = () => {
            console.log('User logging out, disconnecting sockets');
            socketInitialized.current = false;
            setIsConnected(false);
            disconnectSupportSocket();
            disconnectSocket();
        };

        const handleBeforeUnload = () => {
            disconnectSupportSocket();
            disconnectSocket();
        };

        // Listen for storage changes (logout in another tab)
        const handleStorageChange = (e) => {
            if (e.key === 'ID' && !e.newValue) {
                console.log('🔄 User logged out in another tab');
                handleLogout();
            }
        };

        // Listen for logout in current tab
        const handleLogoutEvent = () => {
            console.log('🔄 Logout event detected');
            handleLogout();
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('logout', handleLogoutEvent);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('logout', handleLogoutEvent);
        };
    }, []);

    return (
        <SupportSocketContext.Provider value={{ isConnected }}>
            <div data-testid="support-socket-provider" className="contents">{children}</div>
        </SupportSocketContext.Provider>
    );
};

